import {
  IPayableSchema,
  IPayrollSettings,
  ScheduleStatus
} from "@wingspanhq/payments/dist/interfaces";
import { wsMoment } from "@wingspanhq/utils/dist/date/wsMoment";

const UTC_PAYROLL_CUTOFF_HOUR = 17;
const UTC_PAYROLL_CUTOFF_MINUTES = 30;

export const getExactCutOffDate = (cutOffDate: Date) => {
  return new Date(
    Date.UTC(
      cutOffDate.getFullYear(),
      cutOffDate.getMonth(),
      cutOffDate.getDate(),
      UTC_PAYROLL_CUTOFF_HOUR,
      UTC_PAYROLL_CUTOFF_MINUTES
    )
  );
};

export const getNextPayroll = (payrollSettings: IPayrollSettings) =>
  payrollSettings.scheduleDates?.find(scheduleDate =>
    (scheduleDate.status === ScheduleStatus.Pending ||
      scheduleDate.status === ScheduleStatus.Modified) &&
    scheduleDate?.cutOffDate
      ? getExactCutOffDate(scheduleDate.cutOffDate) > new Date()
      : false
  );

export const getNextPayrollCutOffDate = (payrollSettings: IPayrollSettings) =>
  getNextPayroll(payrollSettings)?.cutOffDate;

/**
 * Finds a payroll that is still Pending but whose cutoff has already passed,
 * meaning it's currently being processed (queued). Falls back to the next
 * upcoming payroll if none is currently processing.
 */
export const getCurrentOrNextPayroll = (payrollSettings: IPayrollSettings) => {
  const now = new Date();

  const pendingSchedules = payrollSettings.scheduleDates?.filter(
    scheduleDate =>
      (scheduleDate.status === ScheduleStatus.Pending ||
        scheduleDate.status === ScheduleStatus.Modified) &&
      scheduleDate?.cutOffDate
  );

  const next = pendingSchedules?.find(
    s => getExactCutOffDate(s.cutOffDate!) > now
  );

  // Last pending schedule whose cutoff has passed — the most recent processing run.
  // Only consider it "currently processing" if there's also an upcoming schedule,
  // otherwise all past-cutoff schedules are likely stuck.
  const currentlyProcessing = next
    ? pendingSchedules
        ?.filter(s => getExactCutOffDate(s.cutOffDate!) <= now)
        .pop()
    : undefined;

  return currentlyProcessing ?? next;
};

/**
 * Finds the payroll schedule that will include a given payable.
 *
 * When enableProcessDaysBeforeDue is OFF, the backend applies no date filter —
 * all approved invoices go into the next run. So we return getCurrentOrNextPayroll.
 *
 * When ON, mirrors the backend's bufferedCheckDate logic:
 *   bufferedCheckDate = scheduleDate.date + processDaysBeforeDue + 1 business day
 * A payable belongs to the first pending schedule whose bufferedCheckDate
 * exceeds the payable's effective due date (client.payDate || dueDate).
 */
export const getMatchingPayroll = (
  payrollSettings: IPayrollSettings,
  payable: IPayableSchema
) => {
  if (!payrollSettings.enableProcessDaysBeforeDue) {
    return getCurrentOrNextPayroll(payrollSettings);
  }

  const effectiveDueDate = payable.client.payDate || payable.dueDate;

  const processDaysBeforeDue = payrollSettings.processDaysBeforeDue ?? 0;

  return payrollSettings.scheduleDates?.find(scheduleDate => {
    if (
      scheduleDate.status !== ScheduleStatus.Pending &&
      scheduleDate.status !== ScheduleStatus.Modified
    ) {
      return false;
    }

    const bufferedCheckDate = wsMoment(scheduleDate.date)
      .businessAdd(processDaysBeforeDue + 1)
      .toDate();

    return effectiveDueDate < bufferedCheckDate;
  });
};
